"use client";

import { useEffect } from "react";
import { useAppSettingsStore } from "@/lib/store/app-settings-store";
import {
  DISPLAY_ZOOM_CHANGE_EVENT,
  applyDisplayZoom,
  normalizeDisplayZoom,
} from "@/lib/display-zoom";

/**
 * 表示倍率 (AppSettings.displayZoom) と DOM を突き合わせる。描画はしない。
 *
 * - 設定が読み込めたら DOM に反映する
 *   → Web で選んだ表示サイズが user_settings 経由でデスクトップにも乗る
 *     (localStorage はオリジンが別なので Web ⇄ Tauri で共有されない)
 * - Cmd +/- のショートカット (components/providers.tsx) が投げるイベントを受けて設定に保存する
 *
 * ショートカットの受け口を Providers に残しているのは全ページ共通にするためで、
 * 設定ストアを引き込むのはアプリ本体だけにしたいのでここで分けている。
 */
export function DisplayZoomSync() {
  const displayZoom = useAppSettingsStore((s) => s.settings.displayZoom);
  const isLoaded = useAppSettingsStore((s) => s.isLoaded);

  // 未ロード中は何もしない。ここで既定値を当てると
  // 「起動直後に一瞬 100% に戻ってから拡大される」ちらつきになる
  useEffect(() => {
    if (!isLoaded) return;
    applyDisplayZoom(displayZoom);
  }, [displayZoom, isLoaded]);

  useEffect(() => {
    const handleChange = (event: Event) => {
      const next = normalizeDisplayZoom((event as CustomEvent<number>).detail);
      const store = useAppSettingsStore.getState();
      if (store.isLoaded && store.settings.displayZoom === next) return;

      // 未ロードのまま updateSettings すると、既定値で他の設定項目を
      // 上書きしてしまう (updateSettings は現在の settings とマージするため)
      const persist = () =>
        void useAppSettingsStore
          .getState()
          .updateSettings({ displayZoom: next });
      if (store.isLoaded) persist();
      else void Promise.resolve(store.loadSettings()).then(persist);
    };

    window.addEventListener(DISPLAY_ZOOM_CHANGE_EVENT, handleChange);
    return () =>
      window.removeEventListener(DISPLAY_ZOOM_CHANGE_EVENT, handleChange);
  }, []);

  return null;
}
